'use client';

import { SvgChevronright, SvgWarningFill } from '@common/icon';
import { cn } from '@core/utils';
import type { DetailHomeGiftProductResponse } from '@generated/unauth';

import { CustomLink } from '@/app/_components/CustomLink';
import { Rating } from '@/app/donation/_components/Rating';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { useScrollToSection } from '@/app/donation/_hooks/useScrollToSection';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { PriceInfo } from '../../GiftDetailHeader/_components/PriceInfo';
import { PrefillForm } from '../PrefillForm';
import { DesktopMainImages } from './DesktopMainImages';
import { MobileHero } from './MobileHero';

interface HeaderInfoSectionProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionId: number;
  institutionLogoUrl: string;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  productData?: DetailHomeGiftProductResponse;
  className?: string;
}

export const HeaderInfoSection = ({
  title,
  heroImageUrlList,
  adultYN,
  institutionId,
  institutionLogoUrl,
  institutionName,
  productSaleStatus,
  productName,
  reviewCount,
  reviewRating,
  productNumber,
  requiredDonationAmount,
  exchangeYN,
  onlineProductYN,
  onlineProductDesc,
  productData,
  className,
}: HeaderInfoSectionProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const { scrollToSectionWithTab } = useScrollToSection();

  const isStoppedSale = productSaleStatus === GIFT_SALE_STATUS.STOPPED;

  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-28',
        'tablet:grid-cols-1',
        'mobile:grid-cols-1',
        className,
      )}
    >
      <MobileHero heroImageUrlList={heroImageUrlList} adultYN={adultYN} />
      <DesktopMainImages
        title={title}
        heroImageUrlList={heroImageUrlList}
        adultYN={adultYN}
      />
      <div>
        <section className="flex w-full flex-col px-20">
          <div className="mb-8 flex items-center justify-between gap-4">
            <CustomLink
              href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
                id: institutionId,
              })}
              className="flex items-center gap-4"
            >
              <div className="relative size-28 shrink-0 overflow-hidden rounded-pill border border-Gray-300">
                <Image
                  fill
                  src={institutionLogoUrl}
                  alt={institutionName}
                  className="object-cover object-center"
                  loading="eager"
                  sizes="28px"
                />
              </div>
              <p className="font-600 text-Gray-600 typo-body4">
                {institutionName}
              </p>
              <SvgChevronright className="size-16 text-Gray-600" />
            </CustomLink>
            <ShareButton />
          </div>
          <div className="mb-8 flex justify-between gap-16">
            <h1
              className={cn(
                'flex-1 break-all font-700 text-Gray-900 typo-h3',
                'tablet:font-600 tablet:typo-body2',
                'mobile:font-600 mobile:typo-body2',
                {
                  'text-Gray-400': isStoppedSale,
                },
              )}
            >
              {productName}
            </h1>
          </div>
          <div
            className={cn('mb-28 flex gap-8', 'tablet:gap-4', 'mobile:gap-4')}
          >
            <Rating
              value={reviewRating ?? 0}
              size={isDesktopViewport ? 24 : 12}
            />
            {reviewCount > 0 && (
              <button
                type="button"
                onClick={() => scrollToSectionWithTab('review')}
                className={cn(
                  'font-600 text-Gray-600 underline typo-body3',
                  'tablet:typo-caption1',
                  'mobile:typo-caption1',
                )}
              >
                {reviewCount.toLocaleString()}건 후기 보기
              </button>
            )}
          </div>
          <div className="mb-20 flex items-end justify-between">
            <PriceInfo amount={requiredDonationAmount} />
          </div>
          <div
            className={cn(
              'flex flex-col gap-24 border-t border-Gray-200 py-20',
              'tablet:gap-16',
              'mobile:gap-16',
            )}
          >
            {onlineProductYN ? (
              <div
                className={cn(
                  'flex flex-col gap-12 typo-body3',
                  'tablet:typo-body4',
                  'mobile:typo-body4',
                )}
              >
                <div className="flex items-start gap-16">
                  <p className="w-92 shrink-0 font-400 text-Gray-600">
                    배송정보
                  </p>
                  <p className="font-700 text-Gray-700">온라인 상품</p>
                </div>
                {onlineProductDesc ? (
                  <div className="flex items-start gap-16">
                    <p className="w-92 shrink-0 font-400 text-Gray-600">
                      상품 설명
                    </p>
                    <p className="whitespace-pre-line font-700 text-Gray-700">
                      {onlineProductDesc}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div
                className={cn(
                  'flex flex-col gap-12 typo-body3',
                  'tablet:typo-body4',
                  'mobile:typo-body4',
                )}
              >
                <div className="flex items-center gap-16">
                  <p className="w-92 font-400 text-Gray-600">배송정보</p>
                  <p className="font-700 text-Gray-700">무료배송</p>
                </div>
                <div className="flex gap-16">
                  <p className="w-92 shrink-0 font-400 text-Gray-600">
                    배송안내
                  </p>
                  <div className="flex flex-col gap-4">
                    <p className="font-700 text-Gray-700">3~7일 예상</p>
                  </div>
                </div>
              </div>
            )}
            {!exchangeYN && (
              <div className="gap-8 rounded-xsmall bg-Gray-50 px-20 py-12 font-400 text-Gray-600 flex-center typo-caption1">
                <SvgWarningFill className="size-16 text-B-500" />
                <p>해당 답례품은 반품/교환이 불가능합니다.</p>
              </div>
            )}
          </div>
          <PrefillForm
            productData={productData}
            productNumber={productNumber}
          />
        </section>
      </div>
    </div>
  );
};
